import { useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiAgentGetBetReport, apiAgentExportBetReport, ApiRequestError } from '@/lib/api'
import type { MemberBetSummary, PaginatedResult } from '@nexbit/types'
import { formatCurrency } from '@/lib/utils'
import { format, startOfMonth, endOfDay } from 'date-fns'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertCircle, Search, TrendingUp, ChevronRight, ChevronLeft, Download } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/contexts/language'

function toDateInputValue(d: Date): string {
  return format(d, 'yyyy-MM-dd')
}

const inputClass = cn(
  'h-9 rounded-[var(--radius-sm)] border border-input bg-transparent px-3',
  'font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
)

export default function AgentReportsPage() {
  const t = useT()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [from, setFrom] = useState(searchParams.get('from') ?? toDateInputValue(startOfMonth(new Date())))
  const [to, setTo] = useState(searchParams.get('to') ?? toDateInputValue(endOfDay(new Date())))
  const [data, setData] = useState<PaginatedResult<MemberBetSummary> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  const [searched, setSearched] = useState(false)

  const handleExport = useCallback(async () => {
    setExporting(true)
    setError(null)
    try {
      await apiAgentExportBetReport({
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
      })
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setExporting(false)
    }
  }, [from, to, t])

  const handleSearch = useCallback(async (e: React.FormEvent, p = 1) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setPage(p)
    try {
      const result = await apiAgentGetBetReport({
        from: new Date(from).toISOString(),
        to: new Date(to + 'T23:59:59').toISOString(),
        page: p,
        limit: 20,
      })
      setData(result)
      setSearched(true)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.common.error)
    } finally {
      setLoading(false)
    }
  }, [from, to, t])

  const totalPages = data ? Math.ceil(data.total / data.limit) : 0

  const totals = data?.items.reduce(
    (acc, r) => ({
      bets: acc.bets + r.betCount,
      bet: acc.bet + parseFloat(r.totalBetAmount),
      win: acc.win + parseFloat(r.totalWinAmount),
      net: acc.net + parseFloat(r.netResult),
    }),
    { bets: 0, bet: 0, win: 0, net: 0 },
  )

  function drilldown(m: MemberBetSummary) {
    const q = new URLSearchParams({
      from: new Date(from).toISOString(),
      to: new Date(to + 'T23:59:59').toISOString(),
      username: m.username,
    })
    navigate(`/agent/reports/member/${m.memberId}?${q.toString()}`)
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="mb-5">
        <span className="font-mono text-xs font-bold uppercase tracking-wider text-foreground">
          {t.betReport.title}
        </span>
      </div>

      <form onSubmit={(e) => void handleSearch(e)}>
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)] mb-4">
          <div className="px-4 py-2.5 bg-secondary">
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.common.filter}
            </span>
          </div>
          <div className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.reports.from}</Label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required className={inputClass} />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.reports.to}</Label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} required className={inputClass} />
            </div>
            <Button type="submit" size="sm" className="h-9 font-mono text-xs gap-1.5" disabled={loading}>
              <Search className="h-3.5 w-3.5" />
              {t.common.search}
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-2 p-3 border border-destructive/40 rounded-[var(--radius)] bg-card mb-4" role="alert">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden">
          <div className="px-4 py-2.5 bg-secondary"><Skeleton className="h-3 w-24" /></div>
          <div className="divide-y divide-border">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4 px-4 py-3">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      ) : data !== null ? (
        <div className="border border-border rounded-[var(--radius)] bg-card overflow-hidden shadow-[var(--shadow-sm)]">
          <div className="px-4 py-2.5 bg-secondary flex items-center gap-2">
            <TrendingUp className="h-3.5 w-3.5 text-secondary-foreground/60" />
            <span className="font-mono text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
              {t.common.total}: {data.total}
            </span>
            {searched && data.items.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void handleExport()}
                disabled={exporting}
                className="ml-auto h-7 font-mono text-xs gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                {exporting ? t.reports.exporting : t.reports.exportCsv}
              </Button>
            )}
          </div>

          {data.items.length === 0 ? (
            <div className="py-12 text-center">
              <p className="font-mono text-sm text-muted-foreground">{t.betReport.noActivity}</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.betReport.member}</TableHead>
                    <TableHead className="text-right">{t.betReport.betCount}</TableHead>
                    <TableHead className="text-right">{t.betReport.betVolume}</TableHead>
                    <TableHead className="text-right">{t.betReport.payout}</TableHead>
                    <TableHead className="text-right">{t.betReport.net}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((m) => (
                    <TableRow key={m.memberId} className="cursor-pointer hover:bg-muted/50" onClick={() => drilldown(m)}>
                      <TableCell>
                        <p className="font-mono text-sm font-semibold">{m.username}</p>
                        {m.displayName && <p className="font-mono text-xs text-muted-foreground">{m.displayName}</p>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm text-muted-foreground">
                        {m.betCount > 0 ? m.betCount.toLocaleString() : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {m.betCount > 0 ? formatCurrency(m.totalBetAmount) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {m.betCount > 0 ? formatCurrency(m.totalWinAmount) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {m.betCount > 0 ? (
                          <span className={parseFloat(m.netResult) >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold'}>
                            {formatCurrency(m.netResult)}
                          </span>
                        ) : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {m.betCount > 0 && (
                          <Button variant="ghost" size="sm" className="h-7 gap-1 font-mono text-xs" onClick={(e) => { e.stopPropagation(); drilldown(m) }}>
                            {t.betReport.viewBets}
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totals && totals.bets > 0 && (
                <div className="px-4 py-3 border-t border-border bg-secondary/20 flex flex-wrap gap-x-6 gap-y-1">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t.reports.totals}</span>
                  <span className="font-mono text-xs">Bet: <strong>{formatCurrency(String(totals.bet.toFixed(2)))}</strong></span>
                  <span className="font-mono text-xs">Win: <strong>{formatCurrency(String(totals.win.toFixed(2)))}</strong></span>
                  <span className="font-mono text-xs">
                    Net:{' '}
                    <strong className={totals.net >= 0 ? 'text-success' : 'text-destructive'}>
                      {formatCurrency(String(totals.net.toFixed(2)))}
                    </strong>
                  </span>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
                  <span className="font-mono text-xs text-muted-foreground">{page} / {totalPages}</span>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1}
                      onClick={(e) => void handleSearch(e as unknown as React.FormEvent, page - 1)} aria-label="ก่อน">
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages}
                      onClick={(e) => void handleSearch(e as unknown as React.FormEvent, page + 1)} aria-label="ถัดไป">
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="border border-dashed border-border rounded-[var(--radius)] py-12 text-center">
          <p className="font-mono text-sm text-muted-foreground">กด {t.common.search} เพื่อดูรายงาน</p>
        </div>
      )}
    </div>
  )
}
